import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { drawSprite, type Sprite } from './sprites';

interface Props {
  readonly sprite: Sprite;
  /** Classe portant la taille à l'écran (toujours un multiple entier du pixel). */
  readonly className?: string | undefined;
  readonly label?: string | undefined;
  readonly onClick?: (() => void) | undefined;
}

/**
 * Une carte de sprite rendue dans un canvas à sa taille logique, puis agrandie
 * par le CSS en `image-rendering: pixelated`.
 *
 * Le canvas fait 30 × 19 pixels réels, pas 270 × 180 : c'est le navigateur qui
 * agrandit, sans interpolation. Les pixels restent donc carrés à tous les
 * zooms et à toutes les densités d'écran, ce qu'un dessin agrandi à la main ne
 * garantit pas. La taille affichée est décidée en CSS, toujours en multiples
 * entiers du pixel logique — sinon les pixels bavent (cf. handoff § mobile).
 */
export function PixelSprite({ sprite, className, label, onClick }: Props): ReactNode {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const context = ref.current?.getContext('2d');
    if (!context) return;
    context.imageSmoothingEnabled = false;
    context.clearRect(0, 0, sprite.width, sprite.height);
    drawSprite(context, sprite, 0, 0, 1);
  }, [sprite]);

  const fill: CSSProperties = { width: '100%', height: '100%', display: 'block' };

  if (!onClick) {
    return (
      <canvas
        ref={ref}
        className={className}
        width={sprite.width}
        height={sprite.height}
        role="img"
        aria-label={label}
      />
    );
  }

  return (
    <button type="button" className={className} onClick={onClick} aria-label={label}>
      <canvas
        ref={ref}
        width={sprite.width}
        height={sprite.height}
        style={fill}
        aria-hidden="true"
      />
    </button>
  );
}
